import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  makeTempId,
  buildChoiceLocalizedDetails,
  getPrimaryMenuLanguage,
  getMenuOptionalLanguages,
  getProductDisplayTitle,
  getChoiceDisplayTitle,
} from "./utils";
import { showWizardToast, showWizardValidation } from "../wizardFeedback";
import { formatMoney } from "../../../../../utils/currency";

function countProductsForChoice(choiceId, productChoices) {
  let count = 0;
  Object.values(productChoices || {}).forEach((ids) => {
    if (Array.isArray(ids) && ids.includes(choiceId)) count += 1;
  });
  return count;
}

const Step3Choices = ({ draft, setDraft, goNext, goPrev }) => {
  const { t } = useTranslation();
  const menuPrimaryLanguage = getPrimaryMenuLanguage(draft?.menu);
  const menuOptionalLanguages = getMenuOptionalLanguages(draft?.menu);
  const products = Array.isArray(draft?.products) ? draft.products : [];
  const choices = Array.isArray(draft?.choices) ? draft.choices : [];
  const productChoices = draft?.relations?.productChoices || {};

  const [title, setTitle] = useState("");
  const [minSelected, setMinSelected] = useState("0");
  const [maxSelected, setMaxSelected] = useState("1");
  const [editingChoiceId, setEditingChoiceId] = useState(null);
  const [selectedChoiceIds, setSelectedChoiceIds] = useState(() => new Set());
  const [choicesOpen, setChoicesOpen] = useState(true);
  const choiceFormRef = useRef(null);

  const toggleChoiceSelection = (choiceId) => {
    setSelectedChoiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(choiceId)) next.delete(choiceId);
      else next.add(choiceId);
      return next;
    });
  };

  const getAssignableChoiceIds = (productId, sourceIds) => {
    const assignedOnProduct = new Set(productChoices[productId] || []);
    return [...new Set((sourceIds || []).filter(Boolean))].filter(
      (choiceId) => !assignedOnProduct.has(choiceId)
    );
  };

  const assignChoicesToProduct = (productId, choiceIds) => {
    const uniqueIds = getAssignableChoiceIds(productId, choiceIds);
    if (!uniqueIds.length || !productId) return;

    setDraft((prev) => {
      const rel = prev.relations || {};
      const nextProductChoices = { ...(rel.productChoices || {}) };
      const current = new Set(nextProductChoices[productId] || []);
      uniqueIds.forEach((id) => current.add(id));
      nextProductChoices[productId] = [...current];
      return {
        ...prev,
        relations: { ...rel, productChoices: nextProductChoices },
      };
    });
    showWizardToast(t, "wizardChoicesAssigned", { count: uniqueIds.length });
    setSelectedChoiceIds(new Set());
  };

  const unassignChoiceFromProduct = (productId, choiceId) => {
    setDraft((prev) => {
      const rel = prev.relations || {};
      const nextProductChoices = { ...(rel.productChoices || {}) };
      nextProductChoices[productId] = (nextProductChoices[productId] || []).filter(
        (id) => id !== choiceId
      );
      return {
        ...prev,
        relations: { ...rel, productChoices: nextProductChoices },
      };
    });
  };

  const canSave = Boolean(title.trim()) && Number(maxSelected || 0) > 0;

  const resetChoiceForm = () => {
    setTitle("");
    setMinSelected("0");
    setMaxSelected("1");
    setEditingChoiceId(null);
  };

  useEffect(() => {
    if (!editingChoiceId || !choiceFormRef.current) return;
    choiceFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    document.getElementById("wizard-choice-title")?.focus({ preventScroll: true });
  }, [editingChoiceId]);

  const startEditChoice = (choice) => {
    setEditingChoiceId(choice.tempId);
    setTitle(getChoiceDisplayTitle(choice, menuPrimaryLanguage) || "");
    setMinSelected(String(choice.minSelected ?? 0));
    setMaxSelected(String(choice.maxSelected ?? 1));
  };

  const saveChoice = () => {
    if (!title.trim()) {
      showWizardValidation(t, "wizardChoiceTitleRequired");
      return;
    }

    const min = Number(minSelected || 0);
    const max = Number(maxSelected || 0);
    if (max <= 0) {
      showWizardValidation(t, "wizardChoiceMaxRequired");
      return;
    }
    if (min > max) {
      showWizardValidation(t, "wizardChoiceMinMaxInvalid");
      return;
    }

    const choiceDetails = buildChoiceLocalizedDetails({
      primaryLanguage: menuPrimaryLanguage,
      optionalLanguages: menuOptionalLanguages,
      titles: { [menuPrimaryLanguage]: title },
    });

    const choicePayload = {
      minSelected: min,
      maxSelected: max,
      obligatory: min > 0,
      isFree: false,
      choiceDetails,
    };

    if (editingChoiceId) {
      setDraft((prev) => ({
        ...prev,
        choices: (prev.choices || []).map((choice) =>
          choice.tempId === editingChoiceId ? { ...choice, ...choicePayload } : choice
        ),
      }));
      showWizardToast(t, "wizardChoiceUpdated");
    } else {
      setDraft((prev) => ({
        ...prev,
        choices: [
          ...(Array.isArray(prev.choices) ? prev.choices : []),
          {
            tempId: makeTempId("choice"),
            ...choicePayload,
          },
        ],
      }));
      showWizardToast(t, "wizardChoiceAdded");
    }

    resetChoiceForm();
  };

  const removeChoice = (choiceTempId) => {
    if (editingChoiceId === choiceTempId) {
      resetChoiceForm();
    }
    setDraft((prev) => {
      const next = { ...prev };
      next.choices = (prev.choices || []).filter((c) => c.tempId !== choiceTempId);

      const itemTempIdsToRemove = Array.isArray(prev.relations?.choiceItems?.[choiceTempId])
        ? prev.relations.choiceItems[choiceTempId]
        : [];
      next.items = (prev.items || []).filter((it) => !itemTempIdsToRemove.includes(it.tempId));

      const nextRelations = { ...(prev.relations || {}) };
      nextRelations.choiceItems = { ...(nextRelations.choiceItems || {}) };
      delete nextRelations.choiceItems[choiceTempId];

      nextRelations.productChoices = { ...(nextRelations.productChoices || {}) };
      for (const [productTempId, choiceTempIds] of Object.entries(nextRelations.productChoices)) {
        if (!Array.isArray(choiceTempIds)) continue;
        const filtered = choiceTempIds.filter((id) => id !== choiceTempId);
        if (filtered.length) nextRelations.productChoices[productTempId] = filtered;
        else delete nextRelations.productChoices[productTempId];
      }

      next.relations = nextRelations;
      return next;
    });
    showWizardToast(t, "wizardChoiceDeleted");
  };

  return (
    <>
      <div className="mt-3 start-step-content">
        <div className="start-step-hero">
          <h2 className="start-step-title">
            <span className="step-badge">4</span>
            {t("wizardChoicesHeading")}
          </h2>
          <p className="start-step-subtitle">{t("wizardChoicesSub")}</p>
        </div>

        <div className="start-options">
          <section
            className="start-products-form start-step-hero-scroll-target"
            ref={choiceFormRef}
            aria-label={editingChoiceId ? t("wizardEditChoiceLabel") : t("wizardAddChoiceLabel")}
          >
            <div className="start-options-section-head">
              <span className="start-options-section-head__index">1</span>
              <div>
                <h3 className="start-options-section-head__title">
                  {editingChoiceId ? t("wizardEditChoiceLabel") : t("wizardAddChoiceLabel")}
                </h3>
                <p className="start-options-section-head__hint">{t("wizardChoicesSub")}</p>
              </div>
            </div>
            <div className="start-choice-fields">
              <div className="start-choice-fields__title">
                <label className="start-step1-label" htmlFor="wizard-choice-title">
                  {t("wizardChoiceTitleLabel")}
                  <span className="start-step1-required" aria-hidden="true">*</span>
                </label>
                <input
                  id="wizard-choice-title"
                  className="form-control start-input"
                  placeholder={t("wizardChoiceTitlePlaceholder")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div>
                <label className="start-step1-label" htmlFor="wizard-choice-min">
                  {t("wizardChoiceMinLabel")}
                </label>
                <input
                  id="wizard-choice-min"
                  type="number"
                  min="0"
                  className="form-control start-input"
                  value={minSelected}
                  onChange={(e) => setMinSelected(e.target.value)}
                />
              </div>
              <div>
                <label className="start-step1-label" htmlFor="wizard-choice-max">
                  {t("wizardChoiceMaxLabel")}
                </label>
                <input
                  id="wizard-choice-max"
                  type="number"
                  min="1"
                  className="form-control start-input"
                  value={maxSelected}
                  onChange={(e) => setMaxSelected(e.target.value)}
                />
              </div>
            </div>
            <div className="start-products-form__actions">
              <button
                type="button"
                className="start-products-add"
                disabled={!canSave}
                onClick={saveChoice}
              >
                {editingChoiceId ? t("wizardUpdateChoice") : t("wizardAddChoice")}
              </button>
              {editingChoiceId ? (
                <button type="button" className="start-static-btn" onClick={resetChoiceForm}>
                  {t("cancel")}
                </button>
              ) : null}
            </div>
          </section>

          <section
            className={`start-products-added${choicesOpen ? " is-open" : ""}`}
            aria-label={t("wizardAllChoices")}
          >
            <button
              type="button"
              className="start-products-added__toggle"
              onClick={() => setChoicesOpen((open) => !open)}
              aria-expanded={choicesOpen}
            >
              <span className="start-options-section-head__index">2</span>
              <h3 className="start-products-added__title">{t("wizardAllChoices")}</h3>
              <span className="start-products-added__count">{choices.length}</span>
              <span className="start-products-added__chevron" aria-hidden="true">
                <i className="fa fa-chevron-down" />
              </span>
            </button>
            {choicesOpen ? (
              <div className="start-products-added__body">
                <p className="start-options-hint">{t("wizardChoicesMultiProductHint")}</p>
                {choices.length === 0 ? (
                  <p className="start-products-empty">{t("wizardNoChoicesYet")}</p>
                ) : (
                  <ul className="start-choice-list">
                    {choices.map((choice) => {
                      const isSelected = selectedChoiceIds.has(choice.tempId);
                      const productCount = countProductsForChoice(choice.tempId, productChoices);
                      return (
                        <li
                          key={choice.tempId}
                          className={`start-choice-card${isSelected ? " is-selected" : ""}${
                            editingChoiceId === choice.tempId ? " is-editing" : ""
                          }`}
                        >
                          <button
                            type="button"
                            className="start-choice-card__select"
                            onClick={() => toggleChoiceSelection(choice.tempId)}
                          >
                            <span className={`start-choice-card__check${isSelected ? " is-on" : ""}`}>
                              {isSelected ? <i className="fa fa-check" aria-hidden="true" /> : null}
                            </span>
                            <span className="start-choice-card__info">
                              <span className="start-choice-card__title">
                                {getChoiceDisplayTitle(choice, menuPrimaryLanguage)}
                              </span>
                              <span className="start-choice-card__pills">
                                <span className="start-choice-card__pill">
                                  {t("wizardChoiceMinLabel")} {choice.minSelected ?? 0}
                                </span>
                                <span className="start-choice-card__pill">
                                  {t("wizardChoiceMaxLabel")} {choice.maxSelected ?? 1}
                                </span>
                                {productCount > 0 ? (
                                  <span className="start-choice-card__pill is-count">
                                    {t("wizardChoiceProductCount", { count: productCount })}
                                  </span>
                                ) : null}
                              </span>
                            </span>
                          </button>
                          <div className="start-products-row__actions">
                            <button
                              type="button"
                              className="start-products-row__icon-btn is-edit"
                              onClick={() => startEditChoice(choice)}
                              aria-label={t("edit")}
                              title={t("edit")}
                            >
                              <i className="fa fa-pen" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="start-products-row__icon-btn is-danger"
                              onClick={() => removeChoice(choice.tempId)}
                              aria-label={t("remove")}
                              title={t("remove")}
                            >
                              <i className="fa fa-trash-alt" aria-hidden="true" />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}
          </section>

          <section className="start-products-added" aria-label={t("wizardAssignChoicesToProducts")}>
            <div className="start-options-section-head">
              <span className="start-options-section-head__index">3</span>
              <h3 className="start-products-added__title">{t("wizardAssignChoicesToProducts")}</h3>
              {selectedChoiceIds.size > 0 ? (
                <span className="start-products-added__count">{selectedChoiceIds.size}</span>
              ) : null}
            </div>
            {selectedChoiceIds.size > 0 ? (
              <p className="start-assign-banner">
                {t("wizardAssignChoicesBatch", { count: selectedChoiceIds.size })}
              </p>
            ) : (
              <p className="start-options-hint">{t("wizardChoicesMultiProductHint")}</p>
            )}
            {products.length === 0 ? (
              <p className="start-products-empty">{t("wizardNoProductsToLink")}</p>
            ) : (
              <div className="start-assign-list">
                {products.map((product) => {
                  const assignedIds = productChoices[product.tempId] || [];
                  const hasSelection = selectedChoiceIds.size > 0;
                  const sourceIds = [...selectedChoiceIds];
                  const pendingIds = hasSelection
                    ? getAssignableChoiceIds(product.tempId, sourceIds)
                    : [];
                  const canAssign = hasSelection && pendingIds.length > 0;

                  return (
                    <article key={product.tempId} className="start-assign-card">
                      <div className="start-assign-card__head">
                        <div className="start-assign-card__identity">
                          <span className="start-assign-card__name">
                            {getProductDisplayTitle(product, menuPrimaryLanguage)}
                          </span>
                          <span className="start-assign-card__price">
                            {formatMoney(product.price ?? 0)}
                          </span>
                        </div>
                        {choices.length > 0 ? (
                          <button
                            type="button"
                            className="start-assign-btn"
                            disabled={!canAssign}
                            onClick={() => assignChoicesToProduct(product.tempId, sourceIds)}
                          >
                            {!hasSelection
                              ? t("wizardAssignChoicesBatch", { count: 0 })
                              : pendingIds.length > 0
                                ? t("wizardAssignChoicesBatch", { count: pendingIds.length })
                                : t("wizardChoicesAlreadyOnProduct")}
                          </button>
                        ) : null}
                      </div>
                      <div className="start-assign-chips">
                        {assignedIds.length === 0 ? (
                          <p className="start-assign-empty">{t("wizardNoChoicesYet")}</p>
                        ) : (
                          assignedIds.map((choiceId) => {
                            const choice = choices.find((c) => c.tempId === choiceId);
                            if (!choice) return null;
                            const minSelected = choice.minSelected ?? 0;
                            const maxSelected = choice.maxSelected ?? 1;
                            return (
                              <span key={choiceId} className="start-assign-chip">
                                <i className="fa fa-list-check" aria-hidden="true" />
                                <span className="start-assign-chip__label">
                                  {getChoiceDisplayTitle(choice, menuPrimaryLanguage)}
                                </span>
                                <span className="start-assign-chip__meta">
                                  {minSelected}–{maxSelected}
                                </span>
                                <button
                                  type="button"
                                  className="start-assign-chip__remove"
                                  onClick={() => unassignChoiceFromProduct(product.tempId, choiceId)}
                                  aria-label={t("remove")}
                                  title={t("remove")}
                                >
                                  <i className="fa fa-times" aria-hidden="true" />
                                </button>
                              </span>
                            );
                          })
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      <div className="toolbar toolbar-bottom start-toolbar">
        <button className="btn btn-secondary sw-btn-prev me-1 start-btn-back" onClick={goPrev} type="button">
          ← {t("back")}
        </button>
        <div className="wizard-step-counter">{t("wizardStep4of9")}</div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary sw-btn-skip" onClick={goNext} type="button">
            {t("wizardSkipStep")}
          </button>
          <button className="btn btn-primary sw-btn-next start-btn-next" onClick={goNext} type="button">
            {t("wizardContinue")} <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Step3Choices;
